import { useCallback, useRef, useEffect } from 'react';
import {
  DockviewReact,
  DockviewReadyEvent,
  IDockviewPanelProps,
  DockviewApi,
} from 'dockview-react';
import 'dockview-react/dist/styles/dockview.css';
import { MainContent } from './MainContent';
import { AIPanel } from './AIPanel';
import { SettingsPanel as SettingsPanelComponent } from './SettingsPanel';
// Experimental views
import { JourneyView } from '../views/JourneyView';
import { QueryPerformanceView } from '../views/QueryPerformanceView';
import { LandingPagesView } from '../views/LandingPagesView';
import { QuickWinsView } from '../views/QuickWinsView';
import { OpportunityMatrixView } from '../views/OpportunityMatrixView';
import { MomentumView } from '../views/MomentumView';
import { HealthRadarView } from '../views/HealthRadarView';
import { ConversionFunnelView } from '../views/ConversionFunnelView';
import { AudienceRhythmView } from '../views/AudienceRhythmView';
import { LivePulseView } from '../views/LivePulseView';
import { TrafficAcquisitionView } from '../views/TrafficAcquisitionView';
import { KeywordGalaxyView } from '../views/KeywordGalaxyView';
import { CohortRetentionView } from '../views/CohortRetentionView';
import { EngagementMatrixView } from '../views/EngagementMatrixView';
import { CannibalizationView } from '../views/CannibalizationView';

// Panel components
const DashboardPanel = (_props: IDockviewPanelProps) => {
  return (
    <div className="h-full overflow-auto bg-background p-4">
      <MainContent />
    </div>
  );
};

const AIPanelComponent = (_props: IDockviewPanelProps) => {
  return (
    <div className="h-full overflow-hidden">
      <AIPanel />
    </div>
  );
};

const SettingsPanel = (_props: IDockviewPanelProps) => {
  return (
    <div className="h-full overflow-hidden">
      <SettingsPanelComponent />
    </div>
  );
};

const InsightsPanel = (_props: IDockviewPanelProps) => {
  return (
    <div className="h-full overflow-auto bg-background p-4">
      <h2 className="text-xl font-semibold mb-4">AI Insights</h2>
      <div className="space-y-3">
        <div className="glass-panel p-4 rounded-lg border-l-4 border-blue-500">
          <h3 className="font-medium">Traffic Trend</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Connect your Google Analytics to see AI-generated insights about your traffic patterns.
          </p>
        </div>
        <div className="glass-panel p-4 rounded-lg border-l-4 border-green-500">
          <h3 className="font-medium">Top Opportunities</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Discover keywords and pages with growth potential.
          </p>
        </div>
      </div>
    </div>
  );
};

// Experimental view panels
const JourneyPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <JourneyView />
  </div>
);

const QueryPerformancePanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <QueryPerformanceView />
  </div>
);

const LandingPagesPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <LandingPagesView />
  </div>
);

const QuickWinsPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <QuickWinsView />
  </div>
);

const OpportunityMatrixPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <OpportunityMatrixView />
  </div>
);

const MomentumPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <MomentumView />
  </div>
);

const HealthRadarPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <HealthRadarView />
  </div>
);

const ConversionFunnelPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <ConversionFunnelView />
  </div>
);

const AudienceRhythmPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <AudienceRhythmView />
  </div>
);

const LivePulsePanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <LivePulseView />
  </div>
);

const TrafficAcquisitionPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <TrafficAcquisitionView />
  </div>
);

const KeywordGalaxyPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <KeywordGalaxyView />
  </div>
);

const CohortRetentionPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <CohortRetentionView />
  </div>
);

const EngagementMatrixPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <EngagementMatrixView />
  </div>
);

const CannibalizationPanel = (_props: IDockviewPanelProps) => (
  <div className="h-full overflow-auto bg-background">
    <CannibalizationView />
  </div>
);

// Component registry
const components = {
  dashboard: DashboardPanel,
  ai: AIPanelComponent,
  settings: SettingsPanel,
  insights: InsightsPanel,
  // Experimental views
  journey: JourneyPanel,
  queries: QueryPerformancePanel,
  pages: LandingPagesPanel,
  quickwins: QuickWinsPanel,
  matrix: OpportunityMatrixPanel,
  momentum: MomentumPanel,
  health: HealthRadarPanel,
  funnel: ConversionFunnelPanel,
  rhythm: AudienceRhythmPanel,
  pulse: LivePulsePanel,
  traffic: TrafficAcquisitionPanel,
  galaxy: KeywordGalaxyPanel,
  cohort: CohortRetentionPanel,
  engagement: EngagementMatrixPanel,
  cannibalization: CannibalizationPanel,
};

interface DockLayoutProps {
  onReady?: (api: DockviewApi) => void;
}

export function DockLayout({ onReady }: DockLayoutProps) {
  const apiRef = useRef<DockviewApi | null>(null);

  const handleReady = useCallback(
    (event: DockviewReadyEvent) => {
      apiRef.current = event.api;

      // Create default layout - just the dashboard
      // AI panel can be opened from sidebar when needed
      event.api.addPanel({
        id: 'dashboard',
        component: 'dashboard',
        title: 'Dashboard',
      });

      // Notify parent
      onReady?.(event.api);
    },
    [onReady]
  );

  // Custom theme styles
  useEffect(() => {
    // Apply custom dockview theme
    const style = document.createElement('style');
    style.textContent = `
      .dockview-theme-custom {
        --dv-paneview-active-outline-color: hsl(210 100% 60%);
        --dv-tabs-and-actions-container-background-color: hsl(222 35% 10%);
        --dv-activegroup-visiblepanel-tab-background-color: hsl(222 47% 6%);
        --dv-activegroup-hiddenpanel-tab-background-color: hsl(222 35% 12%);
        --dv-inactivegroup-visiblepanel-tab-background-color: hsl(222 35% 10%);
        --dv-inactivegroup-hiddenpanel-tab-background-color: hsl(222 35% 12%);
        --dv-tab-divider-color: hsl(222 30% 15%);
        --dv-activegroup-visiblepanel-tab-color: hsl(0 0% 98%);
        --dv-activegroup-hiddenpanel-tab-color: hsl(0 0% 65%);
        --dv-inactivegroup-visiblepanel-tab-color: hsl(0 0% 75%);
        --dv-inactivegroup-hiddenpanel-tab-color: hsl(0 0% 55%);
        --dv-separator-border: hsl(222 30% 15%);
        --dv-paneview-header-border-color: hsl(222 30% 15%);
        --dv-icon-hover-background-color: rgba(255, 255, 255, 0.1);
        --dv-floating-box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
        --dv-group-view-background-color: hsl(222 47% 6%);
      }

      .dockview-theme-custom .tab {
        font-size: 13px;
        padding: 0 12px;
      }

      .dockview-theme-custom .tab-content {
        display: flex;
        align-items: center;
        gap: 6px;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <DockviewReact
      className="dockview-theme-custom"
      onReady={handleReady}
      components={components}
    />
  );
}

// Export for use in sidebar actions
export function useDockviewApi() {
  return useRef<DockviewApi | null>(null);
}
